<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="EmploymentStatus2" class="form" data-test-id="employment-status-2-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.EMPLOYMENT_STATUS_2.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="employment-status-2-header">
              <span class="headerlight">
                What's your <strong>employment status?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your employment status...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isEmploymentStatus2Complete && formHidden && !isSyncing"
          :submitted="isEmploymentStatus2Complete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-column class="justify-center full col-12 col-sm-4" v-if="!formHidden && !isSyncing">
          <div class="faux-label required" data-test-id="employment-status-label">Select an Employment Status</div>
            <div>
              <v-select
                v-model="employmentStatus"
                :items="employmentStatuses"
                :backgroundColor="inputBackgroundColor"
                ref="employmentStatusesList"
                outlined
                aria-required="true"
                placeholder="Choose Employment Status"
                dense
                hide-details="auto"
                @change="$v.employmentStatus.$touch()"
                :error-messages="employmentStatusErrors"
                data-test-id="employment-status-select"
              >
              </v-select>
            </div>
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
        </v-column>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="employment-status-2-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS, CLIENT_EMPLOYMENT_STATUSES } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'EmploymentStatus2',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    employmentStatus: null,
    employmentStatuses: [],
    MENUS: MENUS,
  }),

  validations() {
    return {
      employmentStatus: {
        required,
      },
      validationGroup: ['employmentStatus'],
    };
  },

  computed: {
    // Is employment status complete
    isEmploymentStatus2Complete() {
      return this.isPageComplete(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    employmentStatusErrors() {
      const errors = [];
      if (!this.$v.employmentStatus.$dirty) return errors;
      !this.$v.employmentStatus.required &&
        errors.push('A selection is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      let updatedPages = this.pages;

      const isEmployed = 
        this.employmentStatus === CLIENT_EMPLOYMENT_STATUSES.FULL_TIME_EMPLOYED ||
        this.employmentStatus === CLIENT_EMPLOYMENT_STATUSES.PART_TIME_EMPLOYED ||
        this.employmentStatus === CLIENT_EMPLOYMENT_STATUSES.SELF_EMPLOYED;
      
      if (isEmployed && !this.interviewPages.getWithEmploymentPages()) {
        this.interviewPages.addEmploymentPages();
        updatedPages = this.interviewPages.getInterviewPages();
      }
      if (!isEmployed && this.interviewPages.getWithEmploymentPages()) {
        this.interviewPages.removeEmploymentPages();
        updatedPages = this.interviewPages.getInterviewPages();
      }

      let to = route || updatedPages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        status: this.employmentStatus,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(updatedPages[this.pageIndex]);

      this.completeStep(updatedPages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {        
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // Check if page exists AND is complete before hiding form
      const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
      if (page && page.lastCompleted !== null && page.status === 'SYNCED') {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isEmploymentStatus2Complete(newVal) {
      // Only hide form if page actually exists and is complete
      const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
      if (newVal === true && page && page.lastCompleted !== null) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Initialize form data for current interview
    // Check if page exists AND is complete before hiding form
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
  
    const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
    this.formHidden = page && page.lastCompleted !== null && page.status === 'SYNCED';

    // Build employment status options from constants
    this.employmentStatuses = [];
    Object.keys(CLIENT_EMPLOYMENT_STATUSES).map((key) => {
      const val = CLIENT_EMPLOYMENT_STATUSES[key];    
      this.employmentStatuses.push(val);
    });
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // Check if page exists AND is complete before hiding form
      const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
      if (page && page.lastCompleted !== null && page.status === 'SYNCED') {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>